import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrganizationOfficeDto } from 'src/api/FiretrolApiClient';
import { useOffices } from 'src/api/queries/organization/useOffices';
import { useDeleteOffice } from 'src/api/queries/organization/useOfficeMutations';
import { DivisionList } from 'src/app/organization/DivisionList';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

const getColumns = (): MRT_ColumnDef<OrganizationOfficeDto>[] => {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      id: 'divisions',
      header: 'Divisions',
      accessorFn: (row) => row.divisions?.length ?? 0,
      Cell: ({ cell }) => {
        const office = cell.row.original;
        return <DivisionList divisions={office.divisions} maxItems={1} />;
      },
    },
    {
      id: 'modules',
      header: 'Modules',
      enableColumnFilter: false,
      accessorFn: (row) => row.modules?.length ?? 0,
      Cell: ({ cell }) => {
        const count = cell.getValue<number>();
        return <Badge>{`${count} Modules`}</Badge>;
      },
    },
    {
      id: 'users',
      header: 'Users',
      enableColumnFilter: false,
      accessorFn: (row) => row.users?.length ?? 0,
      Cell: ({ cell }) => {
        const count = cell.getValue<number>();
        return <Badge>{`${count} Users`}</Badge>;
      },
    },
  ];
};

export const Offices: React.FC = () => {
  const { data, isLoading, isFetching } = useOffices();
  const offices = data ?? [];
  const deleteOffice = useDeleteOffice();
  const navigate = useNavigate();

  const handleEdit = (office: OrganizationOfficeDto) => {
    navigate(`/settings/offices/edit/${office.id}`);
  };

  const handleCreate = () => {
    navigate('/settings/offices/create');
  };

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'offices',
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const tableExports = useTableExports<OrganizationOfficeDto>(
    'offices',
    (data) => {
      return data.map((item) => ({
        ['Name']: item.name,
        ['Divisions']: item.divisions?.map((d) => d.name).join(' | ') || '',
        ['Modules']: `${item.modules?.length ?? 0} Modules`,
        ['Users']: `${item.users?.length ?? 0} Users`,
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 150 } },
      renderRowActions: ({ row }) => {
        const office = row.original;
        return (
          <Group>
            <EditActionIcon onClick={() => handleEdit(office)} />
            <DeleteActionIcon
              requiredConfirmation={true}
              onDelete={() => deleteOffice.mutate(office)}
            />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: offices,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={handleCreate}>Create Office</CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: offices.length,
    isLoading,
    isFetching,
    isError: false,
    ...tableExports,
  });

  return (
    <>
      <Header
        breadcrumbProps={{ items: [{ text: 'Settings' }, { text: 'Offices' }] }}
        title="Offices"
        helpText="Users can be assigned to one or many offices to limit visibility. Users can only see data belonging to organizations the user has been assigned."
      />

      {table}
    </>
  );
};
